import { FC, Fragment, ReactElement, type JSX } from 'react';

import { addHours, formatAmsterdamTime, isTodayAmsterdam } from '@eneco/common/date';
import { getStaticPrefix } from '@eneco/common/env';
import RichText from '@eneco/components/RichText/RichText';
import { StretchBucket, StretchBucketSkeleton } from '@eneco/components/StretchBucket';
import useDC from '@eneco/dc/useDC';
import { type TransformedSlice, useDynamicPricing } from '@eneco/hooks/dynamicPricing';
import { useFormatter } from '@eneco/i18n';
import { Box, Grid, IconButton, Image, Popover, Stack, Stretch, Text } from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { ElectricityIcon, ErrorIcon, GasIcon, InfoIcon } from '@eneco/sparky/icons';
import { DC_Domain_Models_Products_ProductType } from '@monorepo-types/dc';
import { useContent } from '@sitecore/common';
import { DynamicPriceCardElectricityV2Rendering } from '@sitecore/types/DynamicPriceCardElectricityV2';
import { DynamicPriceCardGasV2Rendering } from '@sitecore/types/DynamicPriceCardGasV2';

const imageHeight = '114px';

const imagePaths: Record<
  string,
  { [key in Extract<DC_Domain_Models_Products_ProductType, 'electricity' | 'gas'>]: string }
> = {
  eneco: {
    electricity: '/dynamicpricing/current-tariff-electricity.svg',
    gas: '/dynamicpricing/current-tariff-gas.svg',
  },
  oxxio: {
    electricity: '/dynamicpricing/current-tariff-electricity.png',
    gas: '/dynamicpricing/current-tariff-gas.png',
  },
};

export type CurrentDynamicPriceCardProps = Pick<CurrentDynamicPriceCardComponentProps, 'product'>;
type CurrentDynamicPriceCardRendering = DynamicPriceCardElectricityV2Rendering | DynamicPriceCardGasV2Rendering;
export const CurrentDynamicPriceCard: FC<CurrentDynamicPriceCardProps> = ({ product }) => {
  const { label } = useDC();
  const { format, currency } = useFormatter();
  const { f, fields } = useContent<CurrentDynamicPriceCardRendering>();
  const { data, isLoading: isDynamicPricingLoading, hasError } = useDynamicPricing(new Date());

  if (isDynamicPricingLoading) {
    return <StretchBucketSkeleton label={f('loadingLabel.value')} />;
  }

  const title = f(`title.value`);
  const pricingDataProduct = data?.find(({ productType }) => productType === product);

  let image: JSX.Element | undefined = (
    <Image src={`${getStaticPrefix()}/images/${label}/dynamicpricing/error.svg`} alt="" height={imageHeight} />
  );
  let secondaryDescription = (
    <Box padding={2}>
      <ErrorIcon size="medium" color="feedbackError" />
    </Box>
  );

  const displayError = !data || hasError || !pricingDataProduct;
  const link =
    f('link.value.text') !== '[link.value.text]'
      ? {
          text: f('link.value.text'),
          type: f('link.value.linktype'),
          href: f('link.value.href'),
        }
      : undefined;

  const typeOfLayout = fields.useVerticalLayout?.value ? 'vertical' : 'horizontal';

  if (displayError) {
    return (
      <CurrentDynamicPriceCardComponent
        layout={typeOfLayout}
        product={product}
        title={title}
        description={f('errorMissingAllDescription.value')}
        secondaryDescription={secondaryDescription}
        footnote={f('errorMissingAllFootnoteText.value')}
        image={image}
        cta={link}
      />
    );
  }

  const relevantPricingDataSlices = getRelevantSlices(pricingDataProduct.slices);

  let description = <RichText html={f('errorMissingAllDescription.value')} />;

  const currentPrice = relevantPricingDataSlices.currentSlice?.price.totalVat?.inclVat;

  if (relevantPricingDataSlices.currentSlice && currentPrice) {
    const nextPriceDate = addHours(relevantPricingDataSlices.currentSlice.start, 1);
    const nextPriceTime = formatAmsterdamTime(nextPriceDate);

    description = (
      <>
        {<RichText html={format(f('description.value'), { nextPriceTime })} />}

        <Text whiteSpace="nowrap">{`${currency.euroFiveDecimals(currentPrice)} / ${
          pricingDataProduct.denotationType
        }`}</Text>
      </>
    );

    const popoverTopic = f(`secondaryDescriptionTitle.value`);

    secondaryDescription = (
      <Popover
        title={popoverTopic}
        trigger={
          <IconButton label={popoverTopic}>
            <InfoIcon size="medium" />
          </IconButton>
        }>
        {<RichText html={f('secondaryDescriptionContent.value')} />}
      </Popover>
    );

    image = imagePaths?.[label]?.[product] ? (
      <Image src={`${getStaticPrefix()}/images/${label}/${imagePaths[label][product]}`} alt="" height={imageHeight} />
    ) : undefined;
  }

  let footnote = (
    <RichText
      html={currentPrice ? f('errorMissingNextPriceFootnoteText.value') : f('errorMissingAllFootnoteText.value')}
    />
  );

  const nextPrice = relevantPricingDataSlices.nextSlice?.price.totalVat?.inclVat;

  if (relevantPricingDataSlices.nextSlice && nextPrice) {
    const dayIndication = isTodayAmsterdam(relevantPricingDataSlices.nextSlice.start)
      ? f('todayText.value')
      : f('tomorrowText.value');
    const nextPriceTime = formatAmsterdamTime(relevantPricingDataSlices.nextSlice.start);

    footnote = (
      <>
        {
          <RichText
            html={format(f('footnoteText.value'), {
              dayIndication,
              nextPriceTime,
            })}
          />
        }

        <Text whiteSpace="nowrap">{`${currency.euroFiveDecimals(nextPrice)} / ${
          pricingDataProduct.denotationType
        }`}</Text>
      </>
    );
  }

  return (
    <CurrentDynamicPriceCardComponent
      layout={typeOfLayout}
      product={product}
      title={title}
      description={description}
      secondaryDescription={secondaryDescription}
      footnote={footnote}
      image={image}
      cta={link}
    />
  );
};

type CurrentDynamicPriceCardComponentProps = {
  layout?: 'horizontal' | 'vertical';
  product: 'electricity' | 'gas';
  title: string;
  description: string | ReactElement;
  secondaryDescription: ReactElement;
  footnote: string | ReactElement;
  image?: ReactElement;
  cta?: { text: string; type: string; href: string };
};

const CurrentDynamicPriceCardComponent: FC<CurrentDynamicPriceCardComponentProps> = ({
  layout = 'horizontal',
  product,
  title,
  description,
  secondaryDescription,
  footnote,
  image,
  cta,
}) => {
  const mobileLayoutApplied = !useMediaQuery('md');

  const textContent = (
    <Stack.Item grow="false" key="textContent">
      <Grid
        gap={1}
        alignY="center"
        gridTemplateAreas={`
                "icon description"
                "  .   footnote  "
              `}>
        <Grid.Item gridArea="icon">
          {product === 'electricity' ? <ElectricityIcon color="iconElectricity" /> : <GasIcon color="iconGas" />}
        </Grid.Item>
        <Grid.Item gridArea="description">
          <Stack direction="row" alignY="center">
            <Text size="BodyL" weight="bold" as="p">
              {description}
            </Text>
            {secondaryDescription}
          </Stack>
        </Grid.Item>
        <Grid.Item gridArea="footnote">
          <Text size="BodyS" as="p">
            {footnote}
          </Text>
        </Grid.Item>
      </Grid>
    </Stack.Item>
  );

  const imageContent = <Fragment key="imageContent">{!mobileLayoutApplied && image}</Fragment>;
  const allContent = [textContent, imageContent];

  if (layout === 'vertical') {
    allContent.reverse();
  }

  return (
    <StretchBucket title={title} buttonText={cta?.text} buttonLinkType={cta?.type} buttonLink={cta?.href}>
      <Stretch height>
        <Stack
          gap={6}
          direction={layout === 'horizontal' ? 'row' : 'column'}
          alignX={layout === 'horizontal' ? 'justify' : 'center'}
          alignY={layout === 'horizontal' ? 'center' : 'start'}>
          {allContent}
        </Stack>
      </Stretch>
    </StretchBucket>
  );
};

const getRelevantSlices = (slices: TransformedSlice[] | undefined) => {
  if (!slices) {
    return {
      currentSlice: undefined,
      nextSlice: undefined,
    };
  }
  const indexCurrentSlice = slices.findIndex(slice => slice.isCurrentHour);
  return {
    currentSlice: slices[indexCurrentSlice],
    nextSlice: indexCurrentSlice === -1 ? undefined : slices[indexCurrentSlice + 1],
  };
};
